import { Growls } from "PFApp/components/growls";
import { TermsOfServiceModal } from "PFApp/layout/footer/parts/terms_of_service_modal";
import { useAppShellStore } from "PFApp/stores/app_shell_store";
import { useTheme } from "PFApp/theme_context_provider/theme_context_provider_context";
import { ActionDiv } from "PFComponents/containers";
import { useDebounce } from "PFCore/helpers/use_debounce";
import { useWindowSize } from "PFCore/helpers/use_window_size";
import { ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";

import css from "./app_shell_layout.module.scss";
import { calculateMainDimensions } from "./app_shell_layout.utils";
import { SkipLinksRegion, SkipToContent } from "./components/skip_to_content";
import { useAppLayoutStore, useAppLayoutStoreActions } from "./stores/app_layout_store";

type AppShellLayoutProps = {
  children?: ReactNode;
};

export const AppShellLayout = ({ children }: AppShellLayoutProps) => {
  const { t } = useTranslation("translation");
  const edgeToEdge = useAppLayoutStore((state) => state.edgeToEdge);
  const isBackdropVisible = useAppLayoutStore((state) => state.isBackdropVisible);
  const isNavBarRevealedOnMobile = useAppLayoutStore((state) => state.isNavBarRevealedOnMobile);
  const layout = useAppLayoutStore((state) => state.layout);
  const leftActionPanel = useAppLayoutStore((state) => state.leftActionPanel);
  const mainLeft = useAppLayoutStore((state) => state.mainLeft);
  const mainRight = useAppLayoutStore((state) => state.mainRight);
  const rightActionPanel = useAppLayoutStore((state) => state.rightActionPanel);
  const termsShown = useAppShellStore((state) => state.termsShown);
  const { hideBackdrop, hideNavBar, setMainDimensions } = useAppLayoutStoreActions();

  const { windowWidth } = useWindowSize();

  const mainStyle = {
    marginLeft: mainLeft,
    marginRight: mainRight
  };

  const recalculate = () => {
    setMainDimensions(
      calculateMainDimensions({
        layout,
        windowWidth,
        leftActionPanel,
        rightActionPanel,
        edgeToEdge
      })
    );
  };

  useEffect(useDebounce(recalculate, 100), [
    layout,
    leftActionPanel.collapsed,
    rightActionPanel.collapsed,
    isNavBarRevealedOnMobile,
    windowWidth
  ]);

  useEffect(() => {
    recalculate();
  }, [edgeToEdge]);

  const { themeVars } = useTheme();

  useEffect(() => {
    document.body.style.backgroundColor = themeVars["paletteNeutral2"];
  }, [themeVars]);

  return (
    <>
      <SkipLinksRegion>
        <SkipToContent targetId="main-content" text={t("skipToMainContent")} />
      </SkipLinksRegion>
      <div id="modal_region">{/* Portal for React components */}</div>
      <div id="propilot_region" />
      {children && (
        <main className="main_region" style={mainStyle} data-qa-id="AppLayout.main">
          {children}
        </main>
      )}
      <Growls />
      {termsShown && <TermsOfServiceModal />}
      {isBackdropVisible && (
        <ActionDiv
          data-qa-id="ReactAppLayout.backdrop"
          className={css.backdrop}
          onClick={() => {
            hideBackdrop();

            if (isNavBarRevealedOnMobile) {
              hideNavBar();
            }
          }}
        />
      )}
    </>
  );
};
